import { ArrowDownward, Info } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import type { BookingPrecheckResponse } from 'api/models/BookingPrecheck.type';
import handleDestinationSupplier from 'components/Bookings/Common/DestinationSupplierCheck';
import DisplayPrice from 'components/Common/DisplayValues/DisplayPrice';
import VehicleDisplayName from 'components/Common/VehicleDisplayName/VehicleDisplayName';
import Counter from 'components/RouteWizard/Counter';
import { useWizard } from 'components/RouteWizard/Wizard';
import useClient from 'hooks/useClient';
import useTravelPolicy, { formatCurrency } from 'hooks/useTravelPolicy';
import QuotesTitle from 'Pages/Client/BookingStatusPage/ReallocateModal/QuotesTitleOld';
import { useState } from 'react';

type QuoteEntryProps = {
  vehicle: any
  vehicleDetails: any
  isImmediateJourney: boolean
  handleBookNow: any
  selectMultiple: any
  selectedVehicles: any
  setSelectedVehicles: any
  canSelectMoreCars: boolean
}

const QuoteEntry = ({
  vehicle,
  isImmediateJourney,
  handleBookNow,
  selectMultiple,
  selectedVehicles,
  setSelectedVehicles,
  canSelectMoreCars,
}: QuoteEntryProps) => {
  const { data } = useWizard()
  const theme = useTheme()
  const clientSettings = useClient(data.account.clientId)
  const fixedPriceDisplay = clientSettings?.fixedPriceDisplay ?? false
  const displayVATPrice = clientSettings?.displayVATPrice ?? false
  const {
    precheckWizardBooking,
    isPrecheckLoading,
    buildPolicyDialogText,
    buildPolicyMessage,
  } = useTravelPolicy()
  console.log('datta', data)
  const [numOfVehicle, setNumOfVehicle] = useState<number>(0)
  const [policyMessage, setPolicyMessage] = useState<string>('')
  const [showPolicyMessage, setShowPolicyMessage] = useState(false)
  const [policyResult, setPolicyResult] =
    useState<BookingPrecheckResponse | null>(null)
  const [cachedPolicyMessage, setCachedPolicyMessage] = useState<ReturnType<
    typeof buildPolicyMessage
  > | null>(null)
  const vehicleTypeName = vehicle?.vehicleDetails?.vehicleTypeName ?? ''
  const pickupTown = data?.plan?.outbound?.pickup?.town ?? ''
  const pickupCounty = data?.plan?.outbound?.pickup?.county ?? ''
  const isBlackCab = vehicleTypeName.includes('BlackCab')
  const isLondonPickup =
    pickupTown.includes('London') || pickupCounty.includes('London')

  const closePolicyModal = () => {
    setShowPolicyMessage(false)
    setPolicyResult(null)
    setCachedPolicyMessage(null)
  }

  const formatAmount = (value: unknown) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '-'
    return value.toFixed(2)
  }

  const getSelectedAmountText = (mode: 'exVat' | 'vat' | 'incVat') => {
    const price = vehicle?.price
    const currency = vehicle?.estimatedPriceCurrency || price?.currency || 'GBP'
    const value =
      mode === 'exVat'
        ? price?.price
        : mode === 'vat'
        ? price?.vat
        : price?.total

    if (typeof value !== 'number' || !Number.isFinite(value)) return '-'
    return formatCurrency(value, currency, 'en-GB')
  }

  const getAmountOverDisplay = (breach: any): string => {
    if (!breach?.amountOver) return '-'

    const reasonCode = String(breach?.reason?.code ?? '').toUpperCase()
    const amountOver = breach.amountOver
    const amount =
      typeof amountOver === 'number'
        ? amountOver
        : (amountOver as any)?.total ?? 0

    if (reasonCode.includes('DISTANCE')) {
      return `${amount} mile${amount === 1 ? '' : 's'}`
    }

    // Spend breach uses currency
    return formatCurrency(
      amount,
      vehicle?.estimatedPriceCurrency || 'GBP',
      'en-GB',
    )
  }

  const renderSpendPeriods = (
    label: string,
    periods: BookingPrecheckResponse['spend'] extends infer S
      ? S extends { user: infer P }
        ? P
        : any
      : any,
  ) => {
    if (!periods) return null
    const rows: Array<{
      key: string
      title: string
      current?: number
      proposed?: number
      projected?: number
    }> = [
      { key: 'day', title: 'Day', ...(periods as any).day },
      { key: 'week', title: 'Week', ...(periods as any).week },
      { key: 'month', title: 'Month', ...(periods as any).month },
      { key: 'lifetime', title: 'Lifetime', ...(periods as any).lifetime },
    ]

    return (
      <Box sx={{ mt: 2 }}>
        <Accordion>
          <AccordionSummary expandIcon={<ArrowDownward />}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {label}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Table
              size="small"
              sx={{
                '& td': { padding: '8px' },
                '& th': {
                  padding: '8px',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
              }}
            >
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">Spend</TableCell>
                  <TableCell align="right">Proposed </TableCell>
                  <TableCell align="right">Projected</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell align="right">
                      {formatAmount(row.current)}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmount(row.proposed)}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmount(row.projected)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      </Box>
    )
  }

  const selectAndBook = () => {
    if (vehicle.isDestinationLogicQuote) {
      handleDestinationSupplier(() =>
        handleBookNow(
          vehicle?.supplierQuoteReference,
          vehicle?.vehicleDetails?.seats,
          vehicle?.estimatedPriceCurrency,
          vehicle?.price,
          VehicleDisplayName({
            vehicleDetails: vehicle?.vehicleDetails,
          }),
          vehicle?.inboundSupplierQuoteReference,
        ),
      )
    } else {
      handleBookNow(
        vehicle?.supplierQuoteReference,
        vehicle?.vehicleDetails?.seats,
        vehicle?.estimatedPriceCurrency,
        vehicle?.price,
        VehicleDisplayName({
          vehicleDetails: vehicle?.vehicleDetails,
        }),
        vehicle?.inboundSupplierQuoteReference,
      )
    }
  }

  const proceedBooking = async () => {
    console.log('vehicle', vehicle)
    try {
      const precheckResult = await precheckWizardBooking({
        estimatedPrice: vehicle?.price?.total ?? vehicle?.price?.price,
      })

      if (
        (precheckResult as any)?.status === 'COMPLIANT' ||
        (precheckResult as any)?.status === 'NO_POLICY'
      ) {
        selectAndBook()
        return
      }

      setPolicyResult(precheckResult)
      setCachedPolicyMessage(
        buildPolicyMessage(precheckResult, { locale: 'en-GB' }),
      )

      if (precheckResult?.allowed || precheckResult?.overridable) {
        setShowPolicyMessage(true)
      } else {
        setPolicyMessage(
          buildPolicyDialogText(precheckResult, { locale: 'en-GB' }),
        )
        setShowPolicyMessage(true)
      }
    } catch (error) {
      console.error('Precheck failed:', error)
      setPolicyMessage('Unable to process booking precheck. Please try again.')
      setShowPolicyMessage(true)
    }
  }

  const handleVehicleNegativeSelect = () => {
    if (numOfVehicle > 0) {
      setNumOfVehicle((prevValue) => {
        const newCount = prevValue - 1

        if (newCount <= 0) {
          const selectedVehiclesList = selectedVehicles
          delete selectedVehiclesList[vehicle?.supplierQuoteReference]
          setSelectedVehicles({ ...selectedVehiclesList })
        } else {
          const selectedVehiclesList = selectedVehicles
          selectedVehiclesList[vehicle?.supplierQuoteReference] = {
            supplierQuoteReference: vehicle?.supplierQuoteReference,
            inboundSupplierQuoteReference:
              vehicle?.inboundSupplierQuoteReference,
            quantity: newCount,
            price: vehicle?.price,
            priceCurrency: vehicle?.estimatedPriceCurrency,
            seats: vehicle?.vehicleDetails?.seats,
            title: VehicleDisplayName({
              vehicleDetails: vehicle?.vehicleDetails,
            }),
          }
          setSelectedVehicles({ ...selectedVehiclesList })
        }

        return newCount
      })
    }
  }

  const handleVehiclePositiveSelect = () => {
    setNumOfVehicle((prevValue) => {
      const newCount = prevValue + 1

      const selectedVehiclesList = selectedVehicles
      selectedVehiclesList[vehicle?.supplierQuoteReference] = {
        supplierQuoteReference: vehicle?.supplierQuoteReference,
        inboundSupplierQuoteReference: vehicle?.inboundSupplierQuoteReference,
        quantity: newCount,
        price: vehicle?.price,
        priceCurrency: vehicle?.estimatedPriceCurrency,
        seats: vehicle?.vehicleDetails?.seats,
        title: VehicleDisplayName({
          vehicleDetails: vehicle?.vehicleDetails,
        }),
        cancellationPolicy: vehicle?.cancellationPolicy,
        inboundCancellationPolicy: vehicle?.inboundCancellationPolicy,
      }
      setSelectedVehicles({ ...selectedVehiclesList })

      return newCount
    })
  }

  return (
    <>
      <Box
        component="div"
        sx={{
          width: '100%',
          backgroundColor: '#F0F0F0',
          padding: '10px',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: '1px solid #DADCDF',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '125px',
          }}
        >
          {vehicle?.isPreferredSupplier && vehicle?.supplierLogo ? (
            <Box
              component="img"
              src={vehicle?.supplierLogo}
              style={{
                width: '100%',
                margin: 0,
                padding: 0,
              }}
            />
          ) : (
            vehicle?.supplierName && (
              <Typography component="p" sx={{ color: '#A1A1A1' }}>
                {vehicle?.supplierName}
              </Typography>
            )
          )}
          {vehicle?.isDestinationLogicQuote && (
            <Tooltip title="Destination Supplier" arrow placement="top-end">
              <Info
                fontSize="medium"
                sx={(theme) => ({
                  color: theme.palette.info.dark,
                  paddingLeft: '5px',
                  verticalAlign: 'bottom',
                })}
              />
            </Tooltip>
          )}
        </Box>
        {vehicle?.price && (
          <>
            <Typography
              component="p"
              sx={{
                color: 'gray',
                fontSize: '20px',
                fontWeight: 'bold',
                textAlign: 'left',
                flexWrap: 'wrap',
              }}
            >
              {DisplayPrice({
                VATTotalPrice: vehicle?.price,
                priceType: fixedPriceDisplay ? 'exact' : 'range',
                displayVATPrice: displayVATPrice,
              })}
              {isImmediateJourney && (
                <Typography
                  component="p"
                  sx={{
                    color: 'gray',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    textAlign: 'left',
                    marginTop: '-5px',
                  }}
                >
                  eta{' '}
                  {vehicle?.eta == 99
                    ? 'unavailable'
                    : `${vehicle?.eta} minute(s)`}
                </Typography>
              )}
            </Typography>
          </>
        )}

        {selectMultiple ? (
          <Counter
            value={numOfVehicle}
            label="vehicle"
            negativeOnClick={() =>
              numOfVehicle > 0 ? handleVehicleNegativeSelect() : null
            }
            positiveOnClick={() =>
              vehicle?.isDestinationLogicQuote
                ? handleDestinationSupplier(() => handleVehiclePositiveSelect())
                : handleVehiclePositiveSelect()
            }
            positiveDisabled={!canSelectMoreCars}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <Button
              sx={{
                backgroundColor: theme.palette.primary.main,
              }}
              onClick={() => proceedBooking()}
              disabled={isPrecheckLoading}
            >
              {isPrecheckLoading ? (
                <>
                  <CircularProgress
                    size={16}
                    sx={{ color: theme.palette.primary.contrastText, mr: 1 }}
                  />
                  CHECKING...
                </>
              ) : (
                'SELECT & BOOK'
              )}
            </Button>
          </Box>
        )}
      </Box>
      <Box>
        {isBlackCab && isLondonPickup ? (
          <Box sx={{ backgroundColor: '#F0F0F0', display: 'flex', p: 2 }}>
            <Alert severity="info">
              Total includes the TfL-regulated taxi fare plus booking/service
              fees. These may be calculated as a percentage of the fare and/or a
              fixed fee. The full total is shown before you confirm.
            </Alert>
          </Box>
        ) : (
          isBlackCab && (
            <Box sx={{ backgroundColor: '#F0F0F0', display: 'flex', p: 2 }}>
              <Alert severity="info">
                Total includes the taxi fare plus any booking or service fees.
                These may be calculated as a percentage of the fare and/or a
                fixed fee. The full total is shown before you confirm
              </Alert>
            </Box>
          )
        )}
      </Box>
      <Dialog
        open={showPolicyMessage}
        onClose={closePolicyModal}
        aria-labelledby="booking-policy-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="booking-policy-dialog-title">
          Booking Policy Check
        </DialogTitle>
        <DialogContent>
          {policyResult && (
            <Box sx={{ mb: 2 }}>
              {cachedPolicyMessage?.policyName && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Policy: {cachedPolicyMessage.policyName}
                </Typography>
              )}
              {displayVATPrice ? (
                <Typography variant="body2">
                  Selected amount (inc. VAT): {getSelectedAmountText('incVat')}
                </Typography>
              ) : (
                <>
                  <Typography variant="body2">
                    Selected amount (ex. VAT): {getSelectedAmountText('exVat')}
                  </Typography>
                  <Typography variant="body2">
                    VAT: {getSelectedAmountText('vat')}
                  </Typography>
                </>
              )}
              {policyResult.breach?.amountOver ? (
                String(policyResult.breach?.reason?.code ?? '')
                  .toUpperCase()
                  .includes('DISTANCE') ? (
                  <Typography variant="body2" color={theme.palette.error.main}>
                    Distance over policy:{' '}
                    {getAmountOverDisplay(policyResult.breach)}
                  </Typography>
                ) : (
                  <Typography variant="body2" color={theme.palette.error.main}>
                    Amount over policy (inc. VAT):{' '}
                    {getAmountOverDisplay(policyResult.breach)}
                  </Typography>
                )
              ) : null}
            </Box>
          )}
          <DialogContentText sx={{ whiteSpace: 'pre-wrap' }}>
            {policyResult
              ? buildPolicyDialogText(policyResult, { locale: 'en-GB' })
              : policyMessage}
          </DialogContentText>

          {policyResult && (
            <Box sx={{ mt: 2 }}>
              {policyResult.spend && (
                <>
                  {renderSpendPeriods('User spend', policyResult.spend.user)}
                  {renderSpendPeriods('Group spend', policyResult.spend.group)}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePolicyModal} variant="contained">
            Go back
          </Button>
          {policyResult?.overridable || policyResult?.allowed ? (
            <Button
              sx={{ background: theme.palette.primary.main }}
              variant="contained"
              onClick={() => {
                closePolicyModal()
                selectAndBook()
              }}
            >
              Book now
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  )
}

type QuoteListingProps = {
  quotes: any
  isImmediateJourney: boolean
  handleBookNow: any
  selectMultiple: any
  selectedVehicles: any
  setSelectedVehicles: any
  canSelectMoreCars: boolean
  vehicleDetails: any
}

const QuoteListing = ({
  quotes,
  isImmediateJourney,
  handleBookNow,
  selectMultiple,
  selectedVehicles,
  setSelectedVehicles,
  canSelectMoreCars,
  vehicleDetails,
}: QuoteListingProps) => {
  return (
    <Box
      component="div"
      sx={{
        width: '100%',
        maxWidth: '550px',
        marginBottom: '20px',
      }}
    >
      <QuotesTitle
        title={VehicleDisplayName({
          vehicleDetails: vehicleDetails,
        })}
        example={vehicleDetails?.example}
        image={vehicleDetails?.icon}
        seats={vehicleDetails?.seats}
        luggage={vehicleDetails?.luggage}
      />
      <Box component="div">
        {quotes?.map((quote: any) => {
          return (
            <QuoteEntry
              key={quote.supplierQuoteReference}
              vehicle={quote}
              vehicleDetails={vehicleDetails}
              isImmediateJourney={isImmediateJourney}
              handleBookNow={handleBookNow}
              selectMultiple={selectMultiple}
              selectedVehicles={selectedVehicles}
              setSelectedVehicles={setSelectedVehicles}
              canSelectMoreCars={canSelectMoreCars}
            />
          )
        })}
      </Box>
    </Box>
  )
}

export default QuoteListing
